import { editorialStatusBlock, severityTicks, button, hairlineRow } from "../../design/components.js";
import type { HelpRequestView, RequestState } from "./help-model.js";
import type { OfferInput } from "./help-publish.js";
import { participantLabel } from "./thread-attribution.js";

export interface ThreadMessage { author: string; text: string; label?: string; createdAtIso: string }

export interface RequestScreenCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  view: HelpRequestView;
  me: string;
  myCell: string; // coarse fallback cell for the offer when the request cell is empty
  headlineText?: string;
  distanceKm?: number;
  nowMs: number;
  preciseLocation?: string;
  messages: ThreadMessage[];
  offerNotes?: Map<string, string>;
  composeDraft?: string;
  onOffer: (input: OfferInput) => Promise<void>;
  onAccept: (offerId: string, helperDid: string) => Promise<void>;
  onSend: (text: string) => Promise<void>;
  onResend?: (text: string) => Promise<void>;
  onDone: () => Promise<void>;
  onCantMake: () => Promise<void>;
  onDraftChange?: (text: string) => void;
  onBack: () => void;
}

/** Parse a "lat,lng" / "lat, lng" string to coords, or null if not valid coordinates. */
export function parseLatLng(s: string): { lat: number; lng: number } | null {
  const parts = s.split(",");
  if (parts.length !== 2) return null;
  const a = parts[0].trim(), b = parts[1].trim();
  if (a === "" || b === "") return null; // "38.7," must not parse as lng=0
  const lat = Number(a), lng = Number(b);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

const COMPOSER_MAX_H = 120; // px — matches .talk-composer__input max-height; then the textarea scrolls

const TERMINAL: ReadonlySet<RequestState> = new Set<RequestState>(["done", "cancelled"]);
const OPENISH: ReadonlySet<RequestState> = new Set<RequestState>(["open", "reopened"]);

function ago(t: RequestScreenCtx["t"], iso: string, nowMs: number): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return t("incident.detail.ago.now");
  const min = Math.max(0, Math.round((nowMs - then) / 60000));
  if (min < 1) return t("incident.detail.ago.now");
  if (min < 60) return t("incident.detail.ago.min", { n: min });
  return t("incident.detail.ago.hr", { n: Math.round(min / 60) });
}

/** Unified request overlay: one screen that adapts by role + lifecycle state.
 *  Requester sees offers→accept then thread+Done; helper sees offer-form→pending
 *  then thread+Can't-make. Merges the former request-detail + coordination-thread. */
export function requestScreen(ctx: RequestScreenCtx): HTMLElement {
  const { t, view } = ctx;
  const role: "requester" | "helper" = view.creator === ctx.me ? "requester" : "helper";
  const openish = OPENISH.has(view.state);
  const terminal = TERMINAL.has(view.state);
  const accepted = view.acceptedHelpers.length > 0;
  const myOffer = view.offers.find((o) => o.helper === ctx.me);
  const iAmAccepted = view.acceptedHelpers.includes(ctx.me);
  const participant = role === "requester" || iAmAccepted;

  const root = document.createElement("section");
  root.className = "screen request-screen";
  root.setAttribute("data-request-screen", view.requestId);

  // Modal-style header: a ✕ close (top-right) rather than a back button, so the request
  // reads as a dialog. data-action stays "back" so the back-button stack + tests are
  // unaffected. The lifecycle action (Done / Can't-make) lives in the urgency row below.
  const header = document.createElement("div");
  header.className = "request-screen__header";
  const title = document.createElement("div");
  title.className = "bx-microlabel request-screen__title";
  title.textContent = t(`need.${view.need}`).toUpperCase();
  const close = document.createElement("button");
  close.type = "button";
  close.className = "bx-modal__close request-screen__close";
  close.setAttribute("aria-label", t("help.detail.back"));
  close.setAttribute("data-action", "back");
  close.textContent = "✕";
  close.addEventListener("click", ctx.onBack);
  header.append(title, close);
  root.append(header);

  // Header
  const dist = ctx.distanceKm != null ? `${ctx.distanceKm.toFixed(1)} km` : "";
  root.append(editorialStatusBlock({
    microlabel: [t(`need.${view.need}`), dist].filter(Boolean).join(" · "),
    headline: ctx.headlineText || t("help.detail.needLabel", { need: t(`need.${view.need}`).toLowerCase() }),
    subline: t("help.detail.location"),
  }));

  // Urgency + lifecycle action share one row: urgency ticks on the left, the Done /
  // Can't-make action pushed to the right.
  const urgency = document.createElement("div");
  urgency.className = "request-screen__urgency";
  const ul = document.createElement("span");
  ul.className = "bx-microlabel";
  ul.textContent = t("help.detail.urgencyLabel");
  urgency.append(ul, severityTicks(view.urgency, 3));
  if (accepted && !terminal) {
    if (role === "requester") {
      const done = button({ label: t("help.thread.done"), variant: "primary", onClick: () => void ctx.onDone() });
      done.setAttribute("data-action", "done");
      done.classList.add("request-screen__lifecycle");
      urgency.append(done);
    } else if (iAmAccepted) {
      const cant = button({ label: t("help.thread.cantMake"), variant: "secondary", onClick: () => void ctx.onCantMake() });
      cant.setAttribute("data-action", "cant-make");
      cant.classList.add("request-screen__lifecycle");
      urgency.append(cant);
    }
  }
  root.append(urgency);

  // First-report age as a single inline line (was a stacked metrics strip).
  const meta = document.createElement("p");
  meta.className = "bx-microlabel request-screen__meta";
  meta.textContent = `${ago(t, view.createdAtIso, ctx.nowMs)} · ${t("incident.detail.metrics.firstLabel")}`;
  root.append(meta);

  if (terminal) {
    const banner = document.createElement("p");
    banner.className = "bx-microlabel request-screen__resolved";
    banner.setAttribute("data-resolved", view.state);
    banner.textContent = t(view.state === "done" ? "help.screen.resolvedDone" : "help.screen.resolvedCancelled");
    root.append(banner);
  }

  if (role === "requester") {
    if (openish) root.append(renderOffers(ctx));
  } else if (!myOffer && openish) {
    const honesty = document.createElement("p");
    honesty.className = "bx-microlabel request-screen__honesty";
    honesty.textContent = t("help.detail.honesty");
    root.append(honesty, renderOfferForm(ctx));
  } else if (myOffer && !iAmAccepted && !terminal) {
    const pending = document.createElement("p");
    pending.className = "bx-body request-screen__sent";
    pending.setAttribute("data-offer-pending", "");
    pending.textContent = t("help.detail.offerSent");
    root.append(pending);
  }

  // When a coordination thread is shown, bound it like the Talk feed: the message
  // stream scrolls in a flex region and the composer pins to the bottom (CSS via the
  // --threaded modifier), instead of the whole screen being one long scroll.
  if (participant && (accepted || (terminal && view.notes.length > 0))) {
    root.classList.add("request-screen--threaded");
    root.append(renderThread(ctx, role, terminal));
  }

  return root;
}

function renderOffers(ctx: RequestScreenCtx): HTMLElement {
  const { t, view } = ctx;
  const wrap = document.createElement("div");
  wrap.className = "request-screen__offers";
  const title = document.createElement("div");
  title.className = "bx-microlabel";
  title.textContent = t("help.detail.offersTitle");
  wrap.append(title);
  if (view.offers.length === 0) {
    const empty = document.createElement("p");
    empty.className = "bx-body";
    empty.textContent = t("help.detail.offersEmpty");
    wrap.append(empty);
    return wrap;
  }
  // Inline caveat at the moment of risk: accepting releases precise location to an
  // unverified helper and can't be undone (mirrors the You-tab safety disclosure).
  // Shown only while there is a not-yet-accepted offer — i.e. an accept is actionable.
  if (view.offers.some((o) => !view.acceptedHelpers.includes(o.helper))) {
    const caveat = document.createElement("p");
    caveat.className = "bx-microlabel request-screen__accept-caveat";
    caveat.setAttribute("data-accept-caveat", "");
    caveat.textContent = t("help.detail.acceptCaveat");
    wrap.append(caveat);
  }
  for (const offer of view.offers) {
    const isAccepted = view.acceptedHelpers.includes(offer.helper);
    const row = document.createElement("div");
    row.className = "request-screen__offer";
    row.append(hairlineRow({
      leading: offer.tags.map((x) => t(`need.${x}`)).join(" · ") || t(`need.${view.need}`),
      trailing: offer.etaMinutes != null ? `${offer.etaMinutes}m` : "",
      tone: "accent",
    }));
    const accept = button({
      label: isAccepted ? t("help.detail.accepted") : t("help.detail.accept"),
      variant: "primary",
      onClick: () => void ctx.onAccept(offer.offerId, offer.helper),
    });
    accept.setAttribute("data-accept-offer", offer.offerId);
    (accept as HTMLButtonElement).disabled = isAccepted;
    row.append(accept);
    const noteText = ctx.offerNotes?.get(offer.offerId);
    if (noteText) {
      const note = document.createElement("p");
      note.className = "bx-body request-screen__offer-note";
      note.setAttribute("data-offer-note-text", "");
      note.textContent = noteText;
      row.append(note);
    }
    wrap.append(row);
  }
  return wrap;
}

function renderOfferForm(ctx: RequestScreenCtx): HTMLElement {
  const { t, view } = ctx;
  const wrap = document.createElement("div");
  wrap.className = "request-screen__offer-form";
  let open = false;
  let note = "";
  let eta: number | undefined;
  const render = () => {
    wrap.replaceChildren();
    if (!open) {
      const offer = button({ label: t("help.detail.offer"), variant: "primary", onClick: () => { open = true; render(); } });
      offer.setAttribute("data-action", "offer");
      wrap.append(offer);
      return;
    }
    const noteInput = document.createElement("input");
    noteInput.type = "text"; noteInput.maxLength = 120;
    noteInput.placeholder = t("help.detail.notePlaceholder");
    noteInput.setAttribute("data-offer-note", "");
    noteInput.addEventListener("input", () => { note = noteInput.value; });
    const etaInput = document.createElement("input");
    etaInput.type = "number"; etaInput.min = "0";
    etaInput.placeholder = t("help.detail.etaPlaceholder");
    etaInput.setAttribute("data-offer-eta", "");
    etaInput.addEventListener("input", () => { const n = parseInt(etaInput.value, 10); eta = Number.isFinite(n) ? n : undefined; });
    const submit = button({
      label: t("help.detail.submitOffer"),
      variant: "primary",
      onClick: () => void ctx.onOffer({
        requestId: view.requestId, tags: [view.need], cell: view.cell || ctx.myCell,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(eta != null ? { etaMinutes: eta } : {}),
      }).then(() => {
        wrap.replaceChildren();
        const done = document.createElement("p");
        done.className = "bx-body request-screen__sent";
        done.setAttribute("data-offer-pending", "");
        done.textContent = t("help.detail.offerSent");
        wrap.append(done);
      }),
    });
    submit.setAttribute("data-action", "submit-offer");
    wrap.append(noteInput, etaInput, submit);
  };
  render();
  return wrap;
}

function renderThread(ctx: RequestScreenCtx, role: "requester" | "helper", terminal: boolean): HTMLElement {
  const { t } = ctx;
  const wrap = document.createElement("div");
  wrap.className = "request-screen__thread";
  wrap.setAttribute("data-thread", ctx.view.requestId);

  if (ctx.preciseLocation) {
    const pin = document.createElement("div");
    pin.className = "request-screen__precise card";
    pin.setAttribute("data-precise", "");
    const label = document.createElement("div");
    label.className = "bx-microlabel";
    label.textContent = role === "requester" ? t("help.thread.preciseSelf") : t("help.thread.precise");
    const coords = parseLatLng(ctx.preciseLocation);
    if (coords) {
      const nav = document.createElement("a");
      nav.className = "bx-body request-screen__precise-link";
      nav.setAttribute("data-precise-nav", "");
      nav.href = `geo:${coords.lat},${coords.lng}?q=${coords.lat},${coords.lng}`;
      nav.textContent = `${coords.lat}, ${coords.lng}`;
      const map = document.createElement("a");
      map.className = "bx-microlabel request-screen__precise-map";
      map.setAttribute("data-precise-map", "");
      map.href = `https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}#map=17/${coords.lat}/${coords.lng}`;
      map.target = "_blank";
      map.rel = "noopener";
      map.textContent = t("help.thread.openMap");
      pin.append(label, nav, map);
    } else {
      const value = document.createElement("p");
      value.className = "bx-body";
      value.textContent = ctx.preciseLocation;
      pin.append(label, value);
    }
    wrap.append(pin);
  }

  const stream = document.createElement("div");
  stream.className = "request-screen__stream";
  if (ctx.messages.length === 0) {
    const empty = document.createElement("p");
    empty.className = "bx-microlabel";
    empty.textContent = t("help.thread.empty");
    stream.append(empty);
  } else {
    for (const msg of ctx.messages) {
      const bubble = document.createElement("div");
      bubble.className = `talk-msg${msg.author === ctx.me ? " talk-msg--mine" : ""}`;
      const head = document.createElement("div");
      head.className = "talk-msg__head bx-microlabel";
      const who = msg.label ?? participantLabel(msg.author, ctx.view, ctx.me, ctx.t);
      head.textContent = `${who} · ${ago(t, msg.createdAtIso, ctx.nowMs)}`;
      const body = document.createElement("p");
      body.className = "talk-msg__body bx-body";
      body.textContent = msg.text;
      if (msg.author === ctx.me && ctx.onResend && !terminal) {
        const resend = button({ label: t("help.thread.resend"), variant: "secondary",
          onClick: () => void ctx.onResend!(msg.text) });
        resend.setAttribute("data-resend", "");
        resend.classList.add("talk-msg__resend");
        bubble.append(head, body, resend);
      } else {
        bubble.append(head, body);
      }
      stream.append(bubble);
    }
  }
  wrap.append(stream);

  if (terminal) return wrap; // read-only history, no compose/actions

  // Composer mirrors the Talk tab (.talk-composer): an auto-grow textarea that
  // sits at the send button's 48px pill height and grows to a cap, with Enter to
  // send and Shift+Enter for newlines.
  const compose = document.createElement("div");
  compose.className = "request-screen__compose";
  const box = document.createElement("textarea");
  box.setAttribute("data-message-input", "");
  box.className = "talk-composer__input";
  box.rows = 1;
  box.placeholder = t("help.thread.composePlaceholder");
  box.setAttribute("data-compose", "");
  box.value = ctx.composeDraft ?? "";

  const autogrow = () => {
    box.style.height = "auto";
    box.style.height = `${Math.min(box.scrollHeight, COMPOSER_MAX_H)}px`;
  };
  box.addEventListener("input", () => { autogrow(); ctx.onDraftChange?.(box.value); });

  const submit = () => {
    const text = box.value.trim();
    if (!text) return;
    // Clear only on success; keep the text in the box to retry on failure.
    void ctx.onSend(text).then(() => { box.value = ""; ctx.onDraftChange?.(""); autogrow(); });
  };
  box.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  });
  const send = button({ label: t("help.thread.send"), variant: "primary", onClick: submit });
  send.classList.add("talk-composer__send");
  send.setAttribute("data-action", "send");
  compose.append(box, send);
  wrap.append(compose);
  return wrap;
}
